import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/auth';
import { AlertTriangle, ArrowRight } from 'lucide-react';

interface ProfileCompletionModalProps {
    isOpen: boolean;
}

const ProfileCompletionModal: React.FC<ProfileCompletionModalProps> = ({ isOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    // Routes where the modal shouldnt appear
    const excludedRoutes = ['/profile', '/admin/profile', '/author/profile', '/login', '/signup', '/verify-account', '/reset-password', '/change-password'];

    // Check if current path starts with any excluded route (to handle sub-routes if any)
    const isExcluded = excludedRoutes.some(route => location.pathname === route || location.pathname.startsWith(route));

    if (!isOpen || isExcluded) return null;

    const handleGoToProfile = () => {
        const profilePath = user?.role === 'author' ? '/author/profile' : '/admin/profile';
        navigate(profilePath, { state: { fromModal: true } });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
                <div className="bg-white p-6 text-center border-b border-gray-100">
                    <div className="mx-auto bg-yellow-50 w-16 h-16 rounded-full flex items-center justify-center mb-4 border border-yellow-100">
                        <AlertTriangle className="w-10 h-10 text-yellow-500" strokeWidth={2.5} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Update Your Profile</h2>
                    <p className="text-gray-500 mt-2 text-sm font-medium">Action Required to Continue</p>
                </div>

                <div className="p-8">
                    <p className="text-gray-600 text-center mb-8 leading-relaxed">
                        Welcome, <span className="font-semibold text-gray-900">{user?.name}</span>.
                        To ensure you have the best experience and full access to all features,
                        please take a moment to complete your profile information.
                    </p>

                    <button
                        onClick={handleGoToProfile}
                        className="w-full bg-[#088A61] text-white py-3.5 rounded-lg font-semibold 
                     hover:bg-[#067451] transition-colors shadow-lg shadow-green-900/10
                     flex items-center justify-center gap-2 group"
                    >
                        Go to Profile
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfileCompletionModal;
